/** Owns the private admin session and rejects every account except the approved Google identity. */
import {
  GoogleAuthProvider,
  browserLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
  type User,
} from 'firebase/auth';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { firebaseAuth, googleWebClientId } from '../../../../core/config/firebase';

export const ADMIN_EMAIL = 'contact.labibur@gmail.com';

type AdminAuthValue = {
  readonly user: User | null;
  readonly loading: boolean;
  readonly error: string;
  readonly signInWithGoogle: () => Promise<boolean>;
  readonly logOut: () => Promise<void>;
  readonly clearError: () => void;
};

const AdminAuthContext = createContext<AdminAuthValue | null>(null);

function isApprovedAdmin(user: User | null): user is User {
  return Boolean(
    user &&
      user.email?.toLowerCase() === ADMIN_EMAIL &&
      user.emailVerified &&
      user.providerData.some((identity) => identity.providerId === GoogleAuthProvider.PROVIDER_ID),
  );
}

function readableAuthError(error: unknown) {
  if (!(error instanceof Error)) return 'Google sign-in could not be completed. Please try again.';
  if (error.message.includes('popup-closed-by-user')) return 'The Google sign-in window was closed.';
  if (error.message.includes('popup-blocked')) return 'Your browser blocked the sign-in window. Allow popups and try again.';
  if (error.message.includes('unauthorized-domain')) return 'This domain is not authorized in Firebase Authentication.';
  return 'Google sign-in could not be completed. Please try again.';
}

export function AdminAuthProvider({ children }: { readonly children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(
    () =>
      onAuthStateChanged(firebaseAuth, (nextUser) => {
        if (isApprovedAdmin(nextUser)) {
          setUser(nextUser);
          setError('');
          setLoading(false);
          return;
        }

        setUser(null);
        setLoading(false);
        if (nextUser) void signOut(firebaseAuth);
      }),
    [],
  );

  const signInWithGoogle = useCallback(async () => {
    setError('');
    setLoading(true);

    try {
      if (!googleWebClientId.endsWith('.apps.googleusercontent.com')) {
        throw new Error('The Google web client ID is invalid.');
      }

      await setPersistence(firebaseAuth, browserLocalPersistence);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const credential = await signInWithPopup(firebaseAuth, provider);

      if (!isApprovedAdmin(credential.user)) {
        await signOut(firebaseAuth);
        setError(`Access is restricted to ${ADMIN_EMAIL}.`);
        return false;
      }

      setUser(credential.user);
      return true;
    } catch (signInError) {
      setError(readableAuthError(signInError));
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logOut = useCallback(async () => {
    setLoading(true);
    try {
      await signOut(firebaseAuth);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => setError(''), []);

  const value = useMemo<AdminAuthValue>(
    () => ({ user, loading, error, signInWithGoogle, logOut, clearError }),
    [clearError, error, loading, logOut, signInWithGoogle, user],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

// Hooks intentionally share this module with their provider so consumers keep one auth contract.
// eslint-disable-next-line react-refresh/only-export-components
export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error('useAdminAuth must be used within AdminAuthProvider.');
  return context;
}
